import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import ClientHeader from '@/components/feature/ClientHeader';
import { getSessionClient, type ClientAccount } from '@/hooks/useClientAuth';
import { getCurrentRequest, type WithdrawalRequest } from '@/hooks/useWithdrawalFlow';
import { formatDateTime } from '@/utils/format';

export default function WithdrawalValidationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientAccount | null>(null);
  const [request, setRequest] = useState<WithdrawalRequest | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await getSessionClient();
      if (!session) {
        navigate('/');
        return;
      }
      if (active) setClient(session);

      const current = await getCurrentRequest();
      if (!current) {
        navigate('/withdrawal');
        return;
      }
      if (active) setRequest(current);
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  if (!client || !request) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <ClientHeader
        clientName={client.fullName}
        backTo="/dashboard"
        backLabel={t('withdrawal.backDashboard')}
      />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-8 md:px-6 md:py-10">

          <div className="animate-fade-rise space-y-6">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-800">
                <i className="ri-mail-send-line text-sm"></i>
                {t('brand.name')}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                {t('validation.title')}
              </h1>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-5 py-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-mail-check-line text-base"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-primary-800">
                  {t('validation.codeSentTitle')}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-primary-700">
                  {t('validation.codeSentText', { email: request.email })}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="mb-3 flex items-center gap-2">
                <i className="ri-key-2-line text-lg text-foreground-500"></i>
                <span className="text-sm font-semibold text-foreground-800">{t('validation.codeLabel')}</span>
              </div>
              <div className="flex justify-center rounded-md border-2 border-dashed border-primary-300 bg-primary-50 py-8">
                <div className="text-center">
                  <p className="font-heading text-5xl font-bold tracking-[0.3em] text-primary-800">
                    {request.code}
                  </p>
                  <p className="mt-3 text-xs text-foreground-600">
                    {t('validation.validUntil', {
                      date: formatDateTime(request.codeExpiresAt),
                    })}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-xs text-accent-900">
                <i className="ri-information-line mt-0.5 shrink-0"></i>
                <span>{t('validation.note')}</span>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-600">
              <i className="ri-spam-2-line mt-0.5 shrink-0 text-base text-foreground-400"></i>
              {t('validation.notReceived')}
            </div>

            <a
              href="/dashboard"
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-home-4-line"></i>
              {t('validation.backHome')}
            </a>
          </div>
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}